export interface StoredRecordingStudioPart {
  id: number
  name: string
}

export interface StoredRecordingStudioTake {
  id: number
  number: number
  mimeType: string
  recordedAt: string
  blob: Blob
  offsetMs?: number
  timelineStartMs?: number
}

export interface StoredRecordingStudioCompSegment {
  id: number
  takeId: number
  startMs: number
  endMs: number
}

export interface StoredRecordingStudioDraft {
  projectTitle: string
  accompanimentSource: 'NONE' | 'UPLOAD' | 'RECORD'
  uploadedAccompanimentName?: string
  parts: StoredRecordingStudioPart[]
  takesByTrack: Record<string, StoredRecordingStudioTake[]>
  activeTakeByTrack: Record<string, number>
  compEnabledByTrack?: Record<string, boolean>
  compSegmentsByTrack?: Record<string, StoredRecordingStudioCompSegment[]>
  trackMixByKey?: Record<string, { volume: number; muted: boolean; solo: boolean }>
  latencyMode?: 'AUTO' | 'MANUAL'
  manualLatencyMs?: number
  latencyEstimateMs?: number
  countInBeats?: 0 | 2 | 4 | 8
  countInBpm?: number
  partialRecordingEnabled?: boolean
  recordingStartMs?: number
  preRollSeconds?: 0 | 2 | 4 | 8
  updatedAt: string
}

const DB_NAME = 'miosegno-recording-studio'
const DB_VERSION = 1
const STORE_NAME = 'drafts'

export function recordingStudioDraftKey(ownerId: string): string {
  return `owner:${ownerId}:current`
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('INDEXED_DB_UNAVAILABLE'))
      return
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.addEventListener('upgradeneeded', () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    })
    request.addEventListener('success', () => resolve(request.result))
    request.addEventListener('error', () => reject(request.error ?? new Error('INDEXED_DB_OPEN_FAILED')))
  })
}

export async function loadRecordingStudioDraft(ownerId: string): Promise<StoredRecordingStudioDraft | null> {
  const db = await openDatabase()
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const request = tx.objectStore(STORE_NAME).get(recordingStudioDraftKey(ownerId))
      request.addEventListener('success', () => resolve((request.result as StoredRecordingStudioDraft | undefined) ?? null))
      request.addEventListener('error', () => reject(request.error ?? new Error('INDEXED_DB_READ_FAILED')))
    })
  } finally {
    db.close()
  }
}

export async function saveRecordingStudioDraft(ownerId: string, draft: StoredRecordingStudioDraft): Promise<void> {
  const db = await openDatabase()
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      tx.objectStore(STORE_NAME).put(draft, recordingStudioDraftKey(ownerId))
      tx.addEventListener('complete', () => resolve())
      tx.addEventListener('abort', () => reject(tx.error ?? new Error('INDEXED_DB_WRITE_ABORTED')))
      tx.addEventListener('error', () => reject(tx.error ?? new Error('INDEXED_DB_WRITE_FAILED')))
    })
  } finally {
    db.close()
  }
}


// recording-studio multi-project r1
export interface RecordingStudioProjectSummary {
  id: string
  title: string
  createdAt: string
  updatedAt: string
}

interface RecordingStudioProjectIndex {
  currentProjectId: string
  projects: RecordingStudioProjectSummary[]
}

export interface RecordingStudioWorkspace {
  currentProjectId: string
  projects: RecordingStudioProjectSummary[]
  draft: StoredRecordingStudioDraft
  migratedLegacyDraft: boolean
}

export function recordingStudioProjectIndexKey(ownerId: string): string {
  return `owner:${ownerId}:projects`
}

export function recordingStudioProjectKey(ownerId: string, projectId: string): string {
  return `owner:${ownerId}:project:${projectId}`
}

export function createRecordingStudioProjectId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return `project-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

export function createEmptyRecordingStudioDraft(now = new Date().toISOString()): StoredRecordingStudioDraft {
  return {
    projectTitle: '',
    accompanimentSource: 'NONE',
    uploadedAccompanimentName: '',
    parts: [],
    takesByTrack: {},
    activeTakeByTrack: {},
    compEnabledByTrack: {},
    compSegmentsByTrack: {},
    trackMixByKey: {},
    latencyMode: 'AUTO',
    manualLatencyMs: 0,
    latencyEstimateMs: 0,
    countInBeats: 0,
    countInBpm: 120,
    partialRecordingEnabled: false,
    recordingStartMs: 0,
    preRollSeconds: 4,
    updatedAt: now,
  }
}

async function readProjectValue<T>(db: IDBDatabase, key: string): Promise<T | null> {
  return new Promise((resolve, reject) => {
    const request = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(key)
    request.addEventListener('success', () => resolve((request.result as T | undefined) ?? null))
    request.addEventListener('error', () => reject(request.error ?? new Error('INDEXED_DB_READ_FAILED')))
  })
}

async function writeProjectValues(
  db: IDBDatabase,
  entries: Array<{ key: string; value: unknown }>,
  deleteKeys: string[] = [],
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    entries.forEach((entry) => store.put(entry.value, entry.key))
    deleteKeys.forEach((key) => store.delete(key))
    tx.addEventListener('complete', () => resolve())
    tx.addEventListener('abort', () => reject(tx.error ?? new Error('INDEXED_DB_WRITE_ABORTED')))
    tx.addEventListener('error', () => reject(tx.error ?? new Error('INDEXED_DB_WRITE_FAILED')))
  })
}

function projectSummary(projectId: string, draft: StoredRecordingStudioDraft, createdAt: string): RecordingStudioProjectSummary {
  return { id: projectId, title: draft.projectTitle, createdAt, updatedAt: draft.updatedAt }
}

export async function loadRecordingStudioWorkspace(ownerId: string): Promise<RecordingStudioWorkspace> {
  const db = await openDatabase()
  try {
    const indexKey = recordingStudioProjectIndexKey(ownerId)
    const index = await readProjectValue<RecordingStudioProjectIndex>(db, indexKey)
    if (index?.projects.length) {
      const currentProjectId = index.projects.some((project) => project.id === index.currentProjectId)
        ? index.currentProjectId
        : index.projects[0].id
      const draft = await readProjectValue<StoredRecordingStudioDraft>(db, recordingStudioProjectKey(ownerId, currentProjectId))
      return { currentProjectId, projects: index.projects, draft: draft ?? createEmptyRecordingStudioDraft(), migratedLegacyDraft: false }
    }

    const legacy = await readProjectValue<StoredRecordingStudioDraft>(db, recordingStudioDraftKey(ownerId))
    const draft = legacy ?? createEmptyRecordingStudioDraft()
    const currentProjectId = createRecordingStudioProjectId()
    const project = projectSummary(currentProjectId, draft, draft.updatedAt || new Date().toISOString())
    await writeProjectValues(db, [
      { key: recordingStudioProjectKey(ownerId, currentProjectId), value: draft },
      { key: indexKey, value: { currentProjectId, projects: [project] } satisfies RecordingStudioProjectIndex },
    ])
    return { currentProjectId, projects: [project], draft, migratedLegacyDraft: Boolean(legacy) }
  } finally {
    db.close()
  }
}

export async function loadRecordingStudioProject(ownerId: string, projectId: string) {
  const db = await openDatabase()
  try {
    const indexKey = recordingStudioProjectIndexKey(ownerId)
    const index = await readProjectValue<RecordingStudioProjectIndex>(db, indexKey)
    if (!index?.projects.some((project) => project.id === projectId)) throw new Error('RECORDING_PROJECT_NOT_FOUND')
    const draft = await readProjectValue<StoredRecordingStudioDraft>(db, recordingStudioProjectKey(ownerId, projectId))
    if (!draft) throw new Error('RECORDING_PROJECT_DRAFT_NOT_FOUND')
    const nextIndex = { ...index, currentProjectId: projectId }
    await writeProjectValues(db, [{ key: indexKey, value: nextIndex }])
    return { projects: nextIndex.projects, draft }
  } finally {
    db.close()
  }
}

export async function saveRecordingStudioProject(ownerId: string, projectId: string, draft: StoredRecordingStudioDraft) {
  const db = await openDatabase()
  try {
    const indexKey = recordingStudioProjectIndexKey(ownerId)
    const index = await readProjectValue<RecordingStudioProjectIndex>(db, indexKey)
    if (!index) throw new Error('RECORDING_PROJECT_INDEX_NOT_FOUND')
    const previous = index.projects.find((project) => project.id === projectId)
    const summary = projectSummary(projectId, draft, previous?.createdAt ?? draft.updatedAt)
    const projects = index.projects.map((project) => project.id === projectId ? summary : project)
    await writeProjectValues(db, [
      { key: recordingStudioProjectKey(ownerId, projectId), value: draft },
      { key: indexKey, value: { currentProjectId: projectId, projects } satisfies RecordingStudioProjectIndex },
    ])
    return projects
  } finally {
    db.close()
  }
}

export async function createRecordingStudioProject(ownerId: string) {
  const db = await openDatabase()
  try {
    const indexKey = recordingStudioProjectIndexKey(ownerId)
    const index = await readProjectValue<RecordingStudioProjectIndex>(db, indexKey)
    const draft = createEmptyRecordingStudioDraft()
    const currentProjectId = createRecordingStudioProjectId()
    const project = projectSummary(currentProjectId, draft, draft.updatedAt)
    const projects = [...(index?.projects ?? []), project]
    await writeProjectValues(db, [
      { key: recordingStudioProjectKey(ownerId, currentProjectId), value: draft },
      { key: indexKey, value: { currentProjectId, projects } satisfies RecordingStudioProjectIndex },
    ])
    return { currentProjectId, projects, draft }
  } finally {
    db.close()
  }
}

export async function duplicateRecordingStudioProject(ownerId: string, sourceProjectId: string, copiedTitle: string) {
  const db = await openDatabase()
  try {
    const indexKey = recordingStudioProjectIndexKey(ownerId)
    const index = await readProjectValue<RecordingStudioProjectIndex>(db, indexKey)
    const source = await readProjectValue<StoredRecordingStudioDraft>(db, recordingStudioProjectKey(ownerId, sourceProjectId))
    if (!index || !source) throw new Error('RECORDING_PROJECT_NOT_FOUND')
    const now = new Date().toISOString()
    const currentProjectId = createRecordingStudioProjectId()
    const draft: StoredRecordingStudioDraft = {
      ...source,
      projectTitle: copiedTitle,
      parts: source.parts.map((part) => ({ ...part })),
      takesByTrack: Object.fromEntries(Object.entries(source.takesByTrack).map(([key, takes]) => [key, takes.map((take) => ({ ...take }))])),
      activeTakeByTrack: { ...source.activeTakeByTrack },
      trackMixByKey: Object.fromEntries(Object.entries(source.trackMixByKey ?? {}).map(([key, mix]) => [key, { ...mix }])),
      updatedAt: now,
    }
    const project = projectSummary(currentProjectId, draft, now)
    const projects = [...index.projects, project]
    await writeProjectValues(db, [
      { key: recordingStudioProjectKey(ownerId, currentProjectId), value: draft },
      { key: indexKey, value: { currentProjectId, projects } satisfies RecordingStudioProjectIndex },
    ])
    return { currentProjectId, projects, draft }
  } finally {
    db.close()
  }
}

export async function deleteRecordingStudioProject(ownerId: string, projectId: string) {
  const db = await openDatabase()
  try {
    const indexKey = recordingStudioProjectIndexKey(ownerId)
    const index = await readProjectValue<RecordingStudioProjectIndex>(db, indexKey)
    if (!index) throw new Error('RECORDING_PROJECT_INDEX_NOT_FOUND')
    const remaining = index.projects.filter((project) => project.id !== projectId)
    const deleteKeys = [recordingStudioProjectKey(ownerId, projectId)]
    if (remaining.length) {
      const currentProjectId = remaining[0].id
      const draft = await readProjectValue<StoredRecordingStudioDraft>(db, recordingStudioProjectKey(ownerId, currentProjectId))
      if (!draft) throw new Error('RECORDING_PROJECT_DRAFT_NOT_FOUND')
      await writeProjectValues(db, [{ key: indexKey, value: { currentProjectId, projects: remaining } satisfies RecordingStudioProjectIndex }], deleteKeys)
      return { currentProjectId, projects: remaining, draft }
    }

    const draft = createEmptyRecordingStudioDraft()
    const currentProjectId = createRecordingStudioProjectId()
    const project = projectSummary(currentProjectId, draft, draft.updatedAt)
    await writeProjectValues(db, [
      { key: recordingStudioProjectKey(ownerId, currentProjectId), value: draft },
      { key: indexKey, value: { currentProjectId, projects: [project] } satisfies RecordingStudioProjectIndex },
    ], deleteKeys)
    return { currentProjectId, projects: [project], draft }
  } finally {
    db.close()
  }
}

export async function importRecordingStudioProject(
  ownerId: string,
  importedDraft: StoredRecordingStudioDraft,
): Promise<{
  currentProjectId: string
  projects: RecordingStudioProjectSummary[]
  draft: StoredRecordingStudioDraft
}> {
  const created = await createRecordingStudioProject(ownerId)
  const now = new Date().toISOString()
  const draft: StoredRecordingStudioDraft = {
    ...importedDraft,
    parts: importedDraft.parts.map((part) => ({ ...part })),
    takesByTrack: Object.fromEntries(
      Object.entries(importedDraft.takesByTrack).map(([key, takes]) => [
        key,
        takes.map((take) => ({ ...take })),
      ]),
    ),
    activeTakeByTrack: { ...importedDraft.activeTakeByTrack },
    trackMixByKey: Object.fromEntries(
      Object.entries(importedDraft.trackMixByKey ?? {}).map(([key, mix]) => [key, { ...mix }]),
    ),
    updatedAt: now,
  }

  const projects = await saveRecordingStudioProject(
    ownerId,
    created.currentProjectId,
    draft,
  )

  return {
    currentProjectId: created.currentProjectId,
    projects,
    draft,
  }
}
